// Platform copy spec layer for Creative Studio AI Copy.
//
// Single source of truth for, per platform:
//   - copy model: "platform_ad_copy" (the platform consumes separate text
//     fields) vs "creative_guidance" (text is embedded in the creative; we only
//     produce messaging guidance, never "deployable" platform copy)
//   - field names, required/optional, max + recommended character limits
//   - CTA constraints / notes / activation warnings
//
// Limits are baseline platform guidance and should be re-confirmed against live
// platform documentation before being treated as contractual (see `source`).
// This layer is intentionally separate from the creative ELIGIBILITY spec
// registry (creativePlatformSpecs) — eligibility validates the asset; this
// validates the ad COPY. They share no rules.
//
// Field `key` values are canonical and align with the AI Copy variant payload
// keys (headline, primaryText, description, cta) so the UI can look limits up
// directly; guidance-only fields (hook/tagline/embeddedMessage) carry no hard
// platform limit.

const SPEC_SOURCE = "baseline_platform_guidance_v1_confirm_before_activation";

function field(key, label, { required = false, maxChars = null, recommendedChars = null, type = "text" } = {}) {
  return Object.freeze({ key, label, required, maxChars, recommendedChars, type });
}

const META_FIELDS = [
  field("primaryText", "Primary Text", { required: true, maxChars: 2200, recommendedChars: 125 }),
  field("headline", "Headline", { required: true, maxChars: 40, recommendedChars: 40 }),
  field("description", "Description", { required: false, maxChars: 30, recommendedChars: 30 }),
  field("cta", "Call To Action", { required: false, type: "cta" }),
];

export const CREATIVE_COPY_PLATFORM_SPECS = Object.freeze({
  meta: Object.freeze({
    key: "meta",
    label: "Meta",
    copyModel: "platform_ad_copy",
    fields: META_FIELDS,
    cta: Object.freeze({ type: "enum", note: "Meta CTAs are a fixed button set (e.g. Learn More, Sign Up, Shop Now)." }),
    notes: ["Primary text truncates around 125 characters on most placements."],
    activationWarnings: [],
    source: SPEC_SOURCE,
  }),
  facebook: Object.freeze({
    key: "facebook",
    label: "Facebook",
    copyModel: "platform_ad_copy",
    fields: META_FIELDS,
    cta: Object.freeze({ type: "enum", note: "Facebook CTAs are a fixed button set." }),
    notes: ["Primary text truncates around 125 characters in feed."],
    activationWarnings: [],
    source: SPEC_SOURCE,
  }),
  instagram: Object.freeze({
    key: "instagram",
    label: "Instagram",
    copyModel: "platform_ad_copy",
    fields: [
      field("primaryText", "Caption / Primary Text", { required: true, maxChars: 2200, recommendedChars: 125 }),
      field("headline", "Headline", { required: false, maxChars: 40, recommendedChars: 40 }),
      field("cta", "Call To Action", { required: false, type: "cta" }),
    ],
    cta: Object.freeze({ type: "enum", note: "Instagram CTAs are a fixed button set." }),
    notes: ["Caption truncates around 125 characters; up to 30 hashtags allowed."],
    activationWarnings: [],
    source: SPEC_SOURCE,
  }),
  tiktok: Object.freeze({
    key: "tiktok",
    label: "TikTok",
    copyModel: "platform_ad_copy",
    fields: [
      field("primaryText", "Ad Text", { required: true, maxChars: 100, recommendedChars: 80 }),
      field("cta", "Call To Action", { required: false, type: "cta" }),
    ],
    cta: Object.freeze({ type: "enum", note: "TikTok CTAs are a fixed button set." }),
    notes: ["Ad text caps near 100 characters; emojis count toward the limit."],
    activationWarnings: [],
    source: SPEC_SOURCE,
  }),
  youtube: Object.freeze({
    key: "youtube",
    label: "YouTube",
    copyModel: "platform_ad_copy",
    fields: [
      field("headline", "Headline / Title", { required: true, maxChars: 100, recommendedChars: 90 }),
      field("longHeadline", "Long Headline", { required: false, maxChars: 90, recommendedChars: 90 }),
      field("description", "Description", { required: false, maxChars: 5000, recommendedChars: 157 }),
      field("cta", "Call To Action", { required: false, maxChars: 10, type: "cta" }),
    ],
    cta: Object.freeze({ type: "text", note: "CTA button text is limited (~10 characters)." }),
    notes: [
      "Only the first ~157 characters of the description show before truncation.",
      "Long headlines cap at 90 characters and surface where the short headline does not fit.",
    ],
    activationWarnings: [],
    source: SPEC_SOURCE,
  }),
  linkedin: Object.freeze({
    key: "linkedin",
    label: "LinkedIn",
    copyModel: "platform_ad_copy",
    fields: [
      field("primaryText", "Intro Text", { required: true, maxChars: 600, recommendedChars: 150 }),
      field("headline", "Headline", { required: true, maxChars: 200, recommendedChars: 70 }),
      field("description", "Description", { required: false, maxChars: 300, recommendedChars: 100 }),
      field("cta", "Call To Action", { required: false, type: "cta" }),
    ],
    cta: Object.freeze({ type: "enum", note: "LinkedIn CTAs are a fixed button set." }),
    notes: ["Intro text truncates around 150 characters on desktop feed."],
    activationWarnings: [],
    source: SPEC_SOURCE,
  }),
  google_search: Object.freeze({
    key: "google_search",
    label: "Search",
    copyModel: "platform_ad_copy",
    fields: [
      field("headline", "Headline (each)", { required: true, maxChars: 30, recommendedChars: 30 }),
      field("description", "Description (each)", { required: true, maxChars: 90, recommendedChars: 90 }),
      field("path", "Display Path (each)", { required: false, maxChars: 15, recommendedChars: 15 }),
    ],
    cta: Object.freeze({ type: "none", note: "Search has no CTA button; calls to action live inside headlines/descriptions." }),
    notes: [
      "Responsive search ads take up to 15 headlines (30 chars) and 4 descriptions (90 chars).",
      "Sitelink / callout / structured-snippet extensions have their own limits (not modeled here yet).",
    ],
    activationWarnings: [],
    source: SPEC_SOURCE,
  }),
  display: Object.freeze({
    key: "display",
    label: "Display / Programmatic",
    copyModel: "creative_guidance",
    fields: [
      field("headline", "Headline / Tagline Guidance", { recommendedChars: 30 }),
      field("description", "Embedded Message Guidance", { recommendedChars: 90 }),
      field("cta", "CTA Guidance", { recommendedChars: 15 }),
    ],
    cta: Object.freeze({ type: "guidance", note: "Display CTAs are baked into the creative, not a platform field." }),
    notes: ["Display banners embed copy in the creative; these are design guidance, not deployable platform fields."],
    activationWarnings: ["Not platform ad copy — for creative/trafficking guidance only."],
    source: SPEC_SOURCE,
  }),
  ctv: Object.freeze({
    key: "ctv",
    label: "CTV",
    copyModel: "creative_guidance",
    fields: [
      field("headline", "On-screen Message Guidance", { recommendedChars: 40 }),
      field("cta", "CTA Guidance", { recommendedChars: 20 }),
    ],
    cta: Object.freeze({ type: "guidance", note: "CTV messaging is embedded in the video creative." }),
    notes: ["CTV has no platform ad-copy fields; produce on-screen messaging guidance only."],
    activationWarnings: ["Not platform ad copy — for creative guidance only."],
    source: SPEC_SOURCE,
  }),
  dooh: Object.freeze({
    key: "dooh",
    label: "DOOH",
    copyModel: "creative_guidance",
    fields: [
      field("headline", "Out-of-home Message Guidance", { recommendedChars: 30 }),
      field("cta", "CTA Guidance", { recommendedChars: 15 }),
    ],
    cta: Object.freeze({ type: "guidance", note: "DOOH messaging is embedded in the creative." }),
    notes: ["Out-of-home copy must be short and glanceable; embedded in the creative, not a platform field."],
    activationWarnings: ["Not platform ad copy — for creative guidance only."],
    source: SPEC_SOURCE,
  }),
});

const COPY_PLATFORM_ALIASES = Object.freeze({
  meta: "meta",
  facebook: "facebook",
  fb: "facebook",
  instagram: "instagram",
  ig: "instagram",
  tiktok: "tiktok",
  youtube: "youtube",
  yt: "youtube",
  linkedin: "linkedin",
  linkedinads: "linkedin",
  search: "google_search",
  googlesearch: "google_search",
  google_search: "google_search",
  google: "google_search",
  googleads: "google_search",
  sem: "google_search",
  display: "display",
  displayprogrammatic: "display",
  programmatic: "display",
  programmaticdisplay: "display",
  ctv: "ctv",
  connectedtv: "ctv",
  dooh: "dooh",
  ooh: "dooh",
  outofhome: "dooh",
});

export function normalizeCopySpecPlatformKey(value) {
  const compact = String(value || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!compact) return "";
  return COPY_PLATFORM_ALIASES[compact] || "";
}

export function getCopyPlatformSpec(value) {
  const key = normalizeCopySpecPlatformKey(value);
  return key ? CREATIVE_COPY_PLATFORM_SPECS[key] || null : null;
}

export function listCopyPlatformSpecs() {
  return Object.values(CREATIVE_COPY_PLATFORM_SPECS);
}

export function isPlatformAdCopyPlatform(value) {
  const spec = getCopyPlatformSpec(value);
  return Boolean(spec && spec.copyModel === "platform_ad_copy");
}

export function getCopyFieldSpec(platformValue, fieldKey) {
  const spec = getCopyPlatformSpec(platformValue);
  if (!spec) return null;
  return spec.fields.find((f) => f.key === fieldKey) || null;
}

function charCount(value) {
  // Count by Unicode code points so emoji/surrogate pairs count as the platform
  // counts them, not as 2 UTF-16 units.
  return Array.from(String(value ?? "")).length;
}

// Validates a single value against a platform field spec. Returns metadata the
// UI surfaces: count, limits, and a status that gates "deployable".
export function validateCopyField(platformValue, fieldKey, value) {
  const fieldSpec = getCopyFieldSpec(platformValue, fieldKey);
  const count = charCount(value);
  const hasValue = count > 0;

  if (!fieldSpec) {
    return { fieldKey, count, maxChars: null, recommendedChars: null, required: false, status: hasValue ? "ok" : "empty", overMax: false, overRecommended: false, missing: false };
  }

  const overMax = fieldSpec.maxChars != null && count > fieldSpec.maxChars;
  const overRecommended = fieldSpec.recommendedChars != null && count > fieldSpec.recommendedChars;
  const missing = Boolean(fieldSpec.required) && !hasValue;

  let status = "ok";
  if (missing) status = "missing";
  else if (overMax) status = "over_max";
  else if (overRecommended) status = "over_recommended";
  else if (!hasValue) status = "empty";

  return {
    fieldKey,
    label: fieldSpec.label,
    count,
    maxChars: fieldSpec.maxChars,
    recommendedChars: fieldSpec.recommendedChars,
    required: Boolean(fieldSpec.required),
    overMax,
    overRecommended,
    missing,
    status,
  };
}

// Validates a full copy entry (object keyed by field) for a platform. Returns
// per-field validations plus overall flags. `deployable` is true only for
// platform_ad_copy platforms whose required fields are present and within hard
// limits — guidance platforms are never "deployable platform copy".
export function validateCopyEntry(platformValue, values = {}) {
  const spec = getCopyPlatformSpec(platformValue);
  if (!spec) {
    return { platformKey: null, copyModel: null, fields: [], deployable: false, hasErrors: false, isGuidance: false, recognized: false };
  }

  const fields = spec.fields.map((f) => validateCopyField(spec.key, f.key, values[f.key]));
  const hasErrors = fields.some((f) => f.status === "over_max" || f.status === "missing");
  const isGuidance = spec.copyModel === "creative_guidance";

  return {
    platformKey: spec.key,
    platformLabel: spec.label,
    copyModel: spec.copyModel,
    isGuidance,
    recognized: true,
    fields,
    hasErrors,
    deployable: !isGuidance && !hasErrors,
  };
}
